import { useRef, useState } from "react";
import { UploadCloud, X, Trash2, ImageIcon, Scan, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ACCEPTED = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
const MAX = 10;
const MAX_DIM = 1600;       // longest edge after resize
const JPEG_QUALITY = 0.82;  // good readability, small size

// Resize + compress on the client so large mobile screenshots don't create a
// huge upload (which breaks on slow connections -> Cloudflare 520).
const compressImage = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const scale = Math.min(1, MAX_DIM / Math.max(width, height));
        width = Math.round(width * scale);
        height = Math.round(height * scale);
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        try {
          resolve(canvas.toDataURL("image/jpeg", JPEG_QUALITY));
        } catch {
          resolve(e.target.result);
        }
      };
      img.onerror = () => resolve(e.target.result);
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });

export default function UploadZone({ images, setImages, onAnalyze, analyzing }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [processing, setProcessing] = useState(false);

  const addFiles = async (files) => {
    const arr = Array.from(files);
    const valid = arr.filter((f) => ACCEPTED.includes(f.type));
    if (valid.length !== arr.length) toast.error("Sadece PNG, JPG, JPEG, WEBP desteklenir");
    if (images.length + valid.length > MAX) {
      toast.error(`En fazla ${MAX} grafik yükleyebilirsiniz`);
    }
    const slots = MAX - images.length;
    const toAdd = valid.slice(0, slots);
    if (toAdd.length === 0) return;
    setProcessing(true);
    try {
      for (const file of toAdd) {
        const dataUrl = await compressImage(file);
        setImages((prev) =>
          prev.length >= MAX
            ? prev
            : [...prev, { id: `${Date.now()}-${Math.random()}`, name: file.name, dataUrl }]);
      }
    } finally {
      setProcessing(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (e.dataTransfer.files?.length) addFiles(e.dataTransfer.files);
  };

  const remove = (id) => setImages((prev) => prev.filter((i) => i.id !== id));

  return (
    <div className="rounded-2xl border p-5 sm:p-6" style={{ background: "var(--bg-card)", borderColor: "var(--border-subtle)" }}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-bold tracking-tight">Grafik Yükle</h2>
          <p className="text-xs mt-0.5" style={{ color: "var(--text-muted)" }}>
            En fazla {MAX} grafik · {images.length}/{MAX} yüklendi · Coin & zaman dilimi otomatik tespit edilir
          </p>
        </div>
      </div>

      <div
        data-testid="chart-upload-dropzone"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        className="rounded-xl border-2 border-dashed cursor-pointer transition-all py-10 px-4 flex flex-col items-center justify-center text-center"
        style={{
          borderColor: drag ? "#3B82F6" : "var(--border-subtle)",
          background: drag ? "rgba(59,130,246,0.06)" : "var(--bg-main)",
        }}
      >
        <UploadCloud className="w-10 h-10 mb-3 text-blue-500" />
        <p className="text-sm font-medium">Grafikleri sürükleyip bırakın veya tıklayın</p>
        <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>PNG · JPG · JPEG · WEBP</p>
        <input
          ref={inputRef}
          data-testid="chart-file-input"
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/webp"
          multiple
          className="hidden"
          onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
        />
      </div>

      {images.length > 0 && (
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4">
          {images.map((img) => (
            <div key={img.id} data-testid="chart-image-thumbnail" className="relative group rounded-lg overflow-hidden border aspect-square" style={{ borderColor: "var(--border-subtle)" }}>
              <img src={img.dataUrl} alt={img.name} className="w-full h-full object-cover" />
              <button
                data-testid="chart-remove-image-button"
                onClick={() => remove(img.id)}
                className="absolute top-1 right-1 w-6 h-6 rounded-md bg-black/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-3.5 h-3.5 text-white" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-3 mt-5">
        <button
          data-testid="chart-analyze-button"
          onClick={onAnalyze}
          disabled={images.length === 0 || analyzing || processing}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl py-3.5 font-semibold text-sm text-white transition-all hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: "#3B82F6", boxShadow: "0 0 24px rgba(59,130,246,0.3)" }}
        >
          {processing ? <><Loader2 className="w-4 h-4 animate-spin" /> Görseller hazırlanıyor…</> : <><Scan className="w-4 h-4" /> Analiz Et</>}
        </button>
        {images.length > 0 && (
          <button
            data-testid="chart-clear-all-button"
            onClick={() => setImages([])}
            disabled={analyzing}
            className="inline-flex items-center gap-1.5 rounded-xl py-3.5 px-4 font-medium text-sm border transition-colors hover:brightness-125 disabled:opacity-40"
            style={{ borderColor: "var(--border-subtle)", color: "var(--text-secondary)" }}
          >
            <Trash2 className="w-4 h-4" /> Temizle
          </button>
        )}
      </div>

      {images.length === 0 && (
        <p className="flex items-center gap-1.5 justify-center text-xs mt-4" style={{ color: "var(--text-muted)" }}>
          <ImageIcon className="w-3.5 h-3.5" /> Analiz için en az bir grafik yükleyin
        </p>
      )}
    </div>
  );
}
